import type { PlatformAPI, StorageMode, Mode, Theme, Language } from './types'
import { getSetting, setSetting, deleteSetting } from '../db/queries'
import { getCurrentUserId } from '../supabase/client'
import { REMINDER_CHANNELS } from '@/src/shared/types/reminders'
import type { ReminderDelivery } from './types'

/** Erinnerungen über den Main-Prozess: Er hält den Plan und klingelt auch bei geschlossenem Fenster. */
const electronReminders: ReminderDelivery = {
  setPlan: plan => { void window.api?.invoke(REMINDER_CHANNELS.schedule, plan)?.catch(() => {}) },
  takeActions: async () => (await window.api?.invoke(REMINDER_CHANNELS.takeActions)) ?? [],
  onActionsReady: listener => window.api?.receive(REMINDER_CHANNELS.actionsReady, listener) ?? (() => {}),
  test: async () => { await window.api?.invoke(REMINDER_CHANNELS.test) },
  getEnabled: async () => (await window.api?.invoke(REMINDER_CHANNELS.getEnabled)) !== false,
  setEnabled: async enabled => { await window.api?.invoke(REMINDER_CHANNELS.setEnabled, enabled) },
  showWindow: () => { void window.api?.invoke(REMINDER_CHANNELS.showWindow)?.catch(() => {}) },
  getPermission: () => 'granted',
  requestPermission: async () => 'granted',
  clear: async () => { await window.api?.invoke(REMINDER_CHANNELS.clear) },
}

/**
 * Electron Platform Adapter
 * Implementation for Electron desktop app
 */
export class ElectronAdapter implements PlatformAPI {
  platform = 'electron' as const

  reminders = electronReminders

  // ==================== STORAGE MODE ====================

  async getStorageMode(): Promise<StorageMode> {
    const mode = await getSetting('storageMode')
    return (mode as StorageMode) || 'offline'
  }

  async setStorageMode(mode: StorageMode): Promise<void> {
    await setSetting('storageMode', mode)
    // Broadcast to every window so the main window's SyncProvider re-evaluates
    // the sync gate live — the HappyCloud-Sync toggle lives in the standalone
    // preferences window. Dexie stays the source of truth; this is best-effort.
    window.api?.invoke?.('set-storage-mode', mode)?.catch?.(() => {})
  }

  // ==================== MODE ====================

  async getMode(): Promise<Mode> {
    // Try to get from Dexie first
    const localMode = await getSetting('mode')
    if (localMode) return localMode as Mode

    // Fallback to localStorage
    const storedMode = localStorage.getItem('mode')
    return (storedMode as Mode) || 'system'
  }

  async setMode(mode: Mode): Promise<void> {
    // Save to both Dexie and localStorage for redundancy
    await setSetting('mode', mode)
    localStorage.setItem('mode', mode)

    // Notify main process to update nativeTheme (affects native UI elements like traffic lights)
    if (window.api) {
      window.api.invoke('set-mode', mode)
    }

    // If in cloud-sync mode, sync to Supabase happens automatically
  }

  onModeChanged(callback: (mode: Mode) => void): () => void {
    // Listen to storage events for cross-window sync
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'mode' && e.newValue) {
        callback(e.newValue as Mode)
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Listen to IPC events if available
    if (window.api) {
      window.api.receive('mode-changed', callback)
    }

    // Return cleanup function
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      if (window.api) {
        window.api.removeAllListeners('mode-changed')
      }
    }
  }

  // ==================== THEME ====================

  async getTheme(): Promise<Theme> {
    // Prefer the shared settings file (read via IPC) — each BrowserWindow has
    // its own localStorage so it would be stale across windows. localStorage
    // is only the fallback for the synchronous anti-flash inline JS.
    if (window.api) {
      try {
        const fromMain = await window.api.invoke('get-current-theme')
        if (fromMain) return fromMain as Theme
      } catch (e) {
        console.warn('[Theme/adapter.getTheme] IPC get-current-theme failed:', e)
      }
    }
    const stored = localStorage.getItem('theme')
    return (stored as Theme) || 'standard'
  }

  async setTheme(theme: Theme): Promise<void> {
    localStorage.setItem('theme', theme)

    // Broadcast to other Electron windows via IPC. The main process also
    // persists to the shared settings file so newly-opened windows can read
    // the current theme via get-current-theme.
    if (window.api) {
      window.api.invoke('set-theme', theme)
    }
  }

  onThemeChanged(callback: (theme: Theme) => void): () => void {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'theme' && e.newValue) {
        callback(e.newValue as Theme)
      }
    }
    window.addEventListener('storage', handleStorageChange)

    if (window.api) {
      const ipcHandler = (theme: Theme) => {
        // Each Electron BrowserWindow has its OWN localStorage (separate
        // webpack entries → separate origins). Persist locally so the next
        // mount of this window initialises with the correct value.
        try {
          localStorage.setItem('theme', theme)
        } catch {
          /* Privater Modus / Speicher gesperrt — das Thema gilt trotzdem für
             diese Sitzung, nur der nächste Start beginnt wieder beim Standard. */
        }
        callback(theme)
      }
      window.api.receive('theme-changed', ipcHandler)
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      if (window.api) {
        window.api.removeAllListeners('theme-changed')
      }
    }
  }

  // ==================== LANGUAGE ====================

  async getLanguage(): Promise<Language | null> {
    const localLanguage = await getSetting('language')
    if (localLanguage) return localLanguage as Language

    const storedLanguage = localStorage.getItem('language')
    return (storedLanguage as Language) || null
  }

  async setLanguage(language: Language): Promise<void> {
    await setSetting('language', language)
    localStorage.setItem('language', language)

    // Notify main process so it can update its own i18n state (tray, jump list,
    // application menu) AND broadcast `language-changed` to every other window
    // — without this, switching language in the Preferences window would only
    // update that window, and the main app keeps showing the old language.
    if (window.api) {
      window.api.invoke('set-language', language)
    }
  }

  // ==================== WINDOW CONTROLS ====================

  window = {
    minimize: (): void => {
      if (window.api) {
        window.api.invoke('window-minimize')
      }
    },

    maximize: (): void => {
      if (window.api) {
        window.api.invoke('window-maximize-toggle')
      }
    },

    close: (): void => {
      if (window.api) {
        window.api.invoke('window-close')
      }
    },

    isMaximizable: (): boolean => {
      return true
    },

    isMinimizable: (): boolean => {
      return true
    },

    resize: async (width: number, height: number, center = false): Promise<boolean> => {
      if (window.api) {
        try {
          const result = await window.api.invoke('window-resize', width, height, center)
          return result === true
        } catch (error) {
          console.error('[Platform] Window resize error:', error)
          return false
        }
      }
      return false
    }
  }

  minimizeWindow(): void {
    this.window.minimize()
  }

  maximizeWindow(): void {
    this.window.maximize()
  }

  closeWindow(): void {
    this.window.close()
  }

  isMaximizable(): boolean {
    return this.window.isMaximizable()
  }

  isMinimizable(): boolean {
    return this.window.isMinimizable()
  }

  // ==================== EXTERNAL LINKS ====================

  async openExternal(url: string): Promise<void> {
    if (window.api) {
      window.api.openExternal(url)
    } else {
      // Fallback to window.open
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  // ==================== SETTINGS ====================

  async getSetting(key: string): Promise<any> {
    const userId = await getCurrentUserId()
    return getSetting(key, userId || undefined)
  }

  async setSetting(key: string, value: any): Promise<void> {
    const userId = await getCurrentUserId()
    await setSetting(key, value, userId || undefined)
  }

  async deleteSetting(key: string): Promise<void> {
    const userId = await getCurrentUserId()
    await deleteSetting(key, userId || undefined)
  }

  // ==================== DISPLAY MODE (Electron-specific) ====================

  async getDisplayMode(): Promise<string> {
    if (window.api) {
      return window.api.invoke('get-display-mode')
    }
    return 'window'
  }

  async setDisplayMode(mode: string): Promise<void> {
    if (window.api) {
      await window.api.invoke('set-display-mode', mode)
    }
  }
}
